import { Box, Typography } from '@mui/material';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type { GroupedHistogramDataItem } from 'types/FleetOverview.types';
import { transformData } from 'views/FleetComparison/helper';
import { processUtilizationAttributes } from './utils';
import LineChartHOC from './LineChartHOC';
import ChartWrapperCard from './ChartWrapperCard';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const MetricItemChartHistogram = ({
  enableDarkTheme,
  data,
  colorIndex,
  bumperIdMap,
  title,
}: {
  enableDarkTheme: boolean;
  data: GroupedHistogramDataItem[];
  colorIndex: string[];
  bumperIdMap: string[];
  title: string;
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  let transformedData = transformData(data);

  if (title === 'utilization') {
    transformedData = processUtilizationAttributes(transformedData);
  }

  transformedData = [...transformedData].sort((a, b) => {
    // @ts-expect-error ignore this line
    return new Date(a.name) - new Date(b.name);
  });

  return (
    <ChartWrapperCard title={t('histogram')}>
      {data.length > 0 ? (
        <LineChartHOC data={transformedData} xaxisDataKey="name">
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={
              enableDarkTheme
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'
            }
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tickFormatter={(value) => {
              return value;
            }}
          />
          <YAxis
            tick={{
              fill: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.65)',
            }}
            axisLine={{
              stroke: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.15)',
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: enableDarkTheme ? palette.boxBackground : '#fff',
              border: `1px solid ${
                enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)'
              }`,
              borderRadius: '4px',
              color: enableDarkTheme ? '#fff' : '#000',
            }}
          />
          {bumperIdMap.map((itemMap, index) => (
            <Line
              key={itemMap}
              type="monotone"
              dataKey={itemMap}
              name={itemMap}
              stroke={colorIndex[index]}
              strokeWidth={2}
              dot={{ r: 2, fill: colorIndex[index], strokeWidth: 0 }}
              activeDot={{ r: 6, fill: colorIndex[index], strokeWidth: 0 }}
            />
          ))}
        </LineChartHOC>
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(0, 0, 0, 0.5)',
            }}
          >
            {t('no_data_to_display')}
          </Typography>
        </Box>
      )}
    </ChartWrapperCard>
  );
};
